
import Vue, { PropType } from 'vue';
import _trim from 'lodash/trim';
import { mapActions, mapState } from 'vuex';
import { CompanyUserRoleDef } from '@run-diffusion/shared';
import { db, functions } from '@/firebase';
import { TeamUserMixin, get$bindFirestoreOptions } from '@/mixins';
import { SNACKBAR_STATUS } from '@/constants/constants';
import {
	CHANGE_THROWN_MESSAGE,
	CompanyUserRoleOutcome,
	CompanyUserRoleTarget,
	EMPTY_COMPANY_USER_ROLE_TARGET,
	buildChangePayload,
	changeOutcome,
} from './companyUserRoleDialogState';
import ActionsIsland from '@/components/ActionsIsland.vue';
import BaseButton from '@/components/base/BaseButton.vue';
import DialogContent from '@/components/base/DialogContent.vue';
import DialogOrBottomSheet from '@/components/base/DialogOrBottomSheet.vue';

/**
 * Change the company role a person holds, or the one a pending invite will
 * grant: `manageCompanyAdmin` tells the two apart by whether the address has an
 * account. Owned by TeamPage. Nothing is preselected, and the role they hold
 * today stays in the list: choosing it is refused by the callable in its own words.
 */
export default Vue.extend({
	name: 'EditCompanyUserRoleDialog',
	mixins: [
		TeamUserMixin,
	],
	props: {
		value: { type: Boolean, default: false },
		target: {
			type: Object as PropType<CompanyUserRoleTarget>,
			default: () => ({ ...EMPTY_COMPANY_USER_ROLE_TARGET }),
		},
	},
	data () {
		return {
			saving: false,
			selectedRoleDefId: null as string | null,
			companyUserRoleDefs: [] as CompanyUserRoleDef[],
			loadingCompanyUserRoleDefs: false,
		};
	},
	computed: {
		...mapState([
			'team',
		]),
		companyId (): string | null {
			return this.team?.companyId || null;
		},
		targetEmail (): string {
			return _trim(this.target?.email || '');
		},
	},
	watch: {
		value (newVal: boolean) {
			if (newVal) this.init();
			else this.teardown();
		},
	},
	beforeDestroy () {
		this.teardown();
	},
	methods: {
		...mapActions([
			'updateSnackbar',
		]),
		init () {
			this.saving = false;
			this.selectedRoleDefId = null;
			this.bindCompanyUserRoleDefs(this.companyId);
		},
		// Nothing listens while the dialog is closed: it lives as long as TeamPage does.
		teardown () {
			if (this.$firestoreRefs?.companyUserRoleDefs) this.$unbind('companyUserRoleDefs');
			this.companyUserRoleDefs = [];
			this.loadingCompanyUserRoleDefs = false;
		},
		async bindCompanyUserRoleDefs (companyId: string | null) {
			if (!companyId) return;

			this.loadingCompanyUserRoleDefs = true;
			try {
				// index created: companyUserRoleDefs: companyId Ascending isDeleted Ascending createdAt Descending __name__ Descending
				const roleDefsRef = db
					.collection('companyUserRoleDefs')
					.where('companyId', '==', companyId)
					.where('isDeleted', '==', false)
					.orderBy('createdAt', 'desc');
				await this.$bind(
					'companyUserRoleDefs',
					roleDefsRef,
					get$bindFirestoreOptions(),
				);
			} catch (error) {
				console.error('Error binding company user role defs:', error);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Failed to load company roles',
					show: true,
				});
			} finally {
				this.loadingCompanyUserRoleDefs = false;
			}
		},
		onResetAndClose () {
			this.$emit('input', false);
		},
		onDialogInput (value: boolean) {
			if (!value) this.onResetAndClose();
		},
		async onConfirm () {
			if (this.scimProvisioningLocksMembership) {
				this.onResetAndClose();
				return;
			}
			if (this.saving || !this.companyId || !this.selectedRoleDefId || !this.targetEmail) return;

			this.saving = true;
			let outcome: CompanyUserRoleOutcome;
			try {
				const manageCompanyAdminFunction = functions.httpsCallable('manageCompanyAdmin');
				const result = await manageCompanyAdminFunction(buildChangePayload({
					email: this.targetEmail,
					companyId: this.companyId,
					companyUserRoleDefId: this.selectedRoleDefId,
				}));
				// The onCall wrapper answers with no data at all when the use case throws.
				outcome = changeOutcome(result?.data, this.targetEmail, { hasAccount: !!this.target?.userId });
			} catch (error) {
				console.error('Error updating company admin role:', error);
				outcome = { succeeded: false, message: CHANGE_THROWN_MESSAGE };
			}
			this.saving = false;

			this.updateSnackbar({
				status: outcome.succeeded ? SNACKBAR_STATUS.SUCCESS : SNACKBAR_STATUS.ERROR,
				message: outcome.message,
				show: true,
			});
			// A refusal (the role they already hold, the last holder of a required
			// role) leaves the dialog open to pick another.
			if (!outcome.succeeded) return;
			this.$emit('success');
			this.onResetAndClose();
		},
	},
	components: {
		ActionsIsland,
		BaseButton,
		DialogContent,
		DialogOrBottomSheet,
	},
});
